import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import ComboWellSelector from '../components/ComboWellSelector'
import CompareSelect from '../components/CompareSelect'
import KPICard from '../components/KPICard'
import KPITable from '../components/KPITable'
import WellAnalysisChart from '../components/WellAnalysisChart'
import DailyDataTable from '../components/DailyDataTable'
import { exportMultiSheet } from '../utils/exportExcel'

function WellAnalysis() {
    const { theme } = useTheme()
    const isDark = theme === 'dark'

    const [selectorData, setSelectorData] = useState(null)
    const [selectedCombo, setSelectedCombo] = useState(null)
    const [selectedWell, setSelectedWell] = useState(null)
    const [comboData, setComboData] = useState(null)
    const [companyData, setCompanyData] = useState(null)
    const [underperformers, setUnderperformers] = useState(null)
    const [upPeriod, setUpPeriod] = useState('l7d')
    const [upDataDate, setUpDataDate] = useState(null)
    const [loading, setLoading] = useState(true)
    const [comboLoading, setComboLoading] = useState(false)
    const [error, setError] = useState(null)
    const [upSortCol, setUpSortCol] = useState('delta')
    const [upSortAsc, setUpSortAsc] = useState(true)
    const [searchParams, setSearchParams] = useSearchParams()
    const urlApplied = useRef(false)

    // Compare mode (two-item side-by-side)
    const [compareOn, setCompareOn] = useState(false)
    const [selectedComboB, setSelectedComboB] = useState(null)
    const [selectedWellB, setSelectedWellB] = useState(null)
    const [comboDataB, setComboDataB] = useState(null)
    const [comboLoadingB, setComboLoadingB] = useState(false)

    // Fetch hierarchy + company overview + under-performers on mount
    useEffect(() => {
        Promise.all([fetchSelector(), fetchCompany(), fetchUnderperformers()])
            .finally(() => setLoading(false))
    }, [])

    // Apply ?combo= and ?well= URL params once selector data loads
    useEffect(() => {
        if (!selectorData || urlApplied.current) return
        const comboParam = searchParams.get('combo')
        if (comboParam) {
            const match = selectorData.find(
                c => (c.comboName || c.name || c.comboCurveTie || '').toLowerCase() === comboParam.toLowerCase()
            )
            if (match) {
                setSelectedCombo(match.comboName || match.name || match.comboCurveTie)
                const wellParam = searchParams.get('well')
                if (wellParam && match.wells) {
                    const wellMatch = match.wells.find(
                        w => (w.wellName || w.name || w.wolfpakWellName || '').toLowerCase() === wellParam.toLowerCase()
                    )
                    if (wellMatch) setSelectedWell(wellMatch.wellName || wellMatch.name || wellMatch.wolfpakWellName)
                }
            } else {
                const partial = selectorData.find(
                    c => (c.comboName || c.name || c.comboCurveTie || '').toLowerCase().startsWith(comboParam.toLowerCase())
                )
                if (partial) setSelectedCombo(partial.comboName || partial.name || partial.comboCurveTie)
            }
        }
        urlApplied.current = true
    }, [selectorData])

    // Fetch A when its selection changes
    useEffect(() => {
        if (selectedCombo) fetchDetail()
    }, [selectedCombo, selectedWell])

    // Fetch B when its selection changes (compare mode)
    useEffect(() => {
        if (compareOn && selectedComboB) fetchDetailB()
    }, [compareOn, selectedComboB, selectedWellB])

    const fetchSelector = async () => {
        try {
            const res = await fetch(`${apiConfig.baseUrl}/well-analysis`)
            if (!res.ok) throw new Error('Failed to fetch hierarchy')
            const json = await res.json()
            setSelectorData(json.combos || [])
        } catch (err) {
            setError(err.message)
        }
    }

    const fetchCompany = async () => {
        try {
            const res = await fetch(`${apiConfig.baseUrl}/dashboard`)
            if (!res.ok) throw new Error('Failed to fetch company production')
            setCompanyData(await res.json())
        } catch {}
    }

    const fetchUnderperformers = async (period) => {
        const p = period || upPeriod
        try {
            const res = await fetch(`${apiConfig.baseUrl}/well-analysis/underperformers?period=${p}`)
            if (!res.ok) throw new Error('Failed to fetch under-performers')
            const json = await res.json()
            setUnderperformers(json.underperformers || [])
            setUpDataDate(json.dataDate || null)
        } catch {}
    }

    const handlePeriodChange = (p) => {
        setUpPeriod(p)
        fetchUnderperformers(p)
    }

    // Shared fetch — returns detail JSON for a given (combo, well)
    const fetchDetailFor = async (combo, well) => {
        const endpoint = well
            ? `${apiConfig.baseUrl}/well-analysis/well?combo=${encodeURIComponent(combo)}&well=${encodeURIComponent(well)}`
            : `${apiConfig.baseUrl}/well-analysis/combo?combo=${encodeURIComponent(combo)}`
        const res = await fetch(endpoint)
        if (!res.ok) throw new Error('Failed to fetch detail')
        return await res.json()
    }

    const fetchDetail = async () => {
        setComboLoading(true)
        try {
            setComboData(await fetchDetailFor(selectedCombo, selectedWell))
        } catch (err) {
            setError(err.message)
        } finally {
            setComboLoading(false)
        }
    }

    const fetchDetailB = async () => {
        setComboLoadingB(true)
        try {
            setComboDataB(await fetchDetailFor(selectedComboB, selectedWellB))
        } catch (err) {
            setError(err.message)
        } finally {
            setComboLoadingB(false)
        }
    }

    const clearSelection = () => {
        setSelectedCombo(null)
        setSelectedWell(null)
        setComboData(null)
    }

    const toggleCompare = () => {
        if (compareOn) {
            setCompareOn(false)
            setSelectedComboB(null)
            setSelectedWellB(null)
            setComboDataB(null)
        } else {
            setCompareOn(true)
        }
    }

    const handleCompareSelect = (combo, well) => {
        setSelectedComboB(combo)
        setSelectedWellB(well || null)
    }

    const handleDownload = (data, label) => {
        if (!data) return
        const dateStr = new Date().toISOString().slice(0, 10)
        const sheets = []

        // Sheet 1: KPI Variance Summary
        if (data.kpis) {
            const k = data.kpis
            const headers = ['Metric', 'Today Actual', 'Today Forecast', 'Today Variance %',
                'L7D Actual', 'L7D Forecast', 'L7D Variance %',
                'L30D Actual', 'L30D Forecast', 'L30D Variance %']
            const row = (name, field) => [
                name,
                k.today?.[field]?.actual ?? '', k.today?.[field]?.forecast ?? '', k.today?.[field]?.variance != null ? k.today[field].variance : '',
                k.l7d?.[field]?.actual ?? '', k.l7d?.[field]?.forecast ?? '', k.l7d?.[field]?.variance != null ? k.l7d[field].variance : '',
                k.l30d?.[field]?.actual ?? '', k.l30d?.[field]?.forecast ?? '', k.l30d?.[field]?.variance != null ? k.l30d[field].variance : ''
            ]
            sheets.push({ name: 'KPI Variance', data: [headers, row('Oil (BBL)', 'oil'), row('Gas (MCF)', 'gas'), row('Water (BBL)', 'water')] })
        }

        // Sheet 2: Daily Data grid (matches on-screen table: incl. injection, pressure, notes)
        if (data.dailyGrid?.length) {
            const headers = ['Date', 'Oil (BBL)', 'Gas (MCF)', 'Water (BBL)', 'Water Inj (BBL)',
                'Inj Press (psi)', 'Tubing Press (psi)', 'Casing Press (psi)', 'Runtime (hr)', 'Downtime (hr)', 'Downtime Notes']
            const rows = [...data.dailyGrid]
                .sort((a, b) => b.date.localeCompare(a.date))
                .map(d => [
                    d.date, d.oil_bbl ?? '', d.gas_mcf ?? '', d.water_bbl ?? '', d.inj_water_bbl ?? '',
                    d.inj_pressure_psi ?? '', d.tubing_pressure_psi ?? '', d.casing_pressure_psi ?? '',
                    d.runtime_hours ?? '', d.downtime_hours ?? '', d.notes ?? ''
                ])
            sheets.push({ name: 'Daily Data', data: [headers, ...rows] })
        }

        // Sheet 3: Production History (merged actuals + forecasts)
        const actual = data.production?.actual || []
        const forecast = data.production?.forecast || []
        if (actual.length || forecast.length) {
            const fcstMap = {}
            forecast.forEach(f => { fcstMap[f.date] = f })
            const allDates = [...new Set([...actual.map(a => a.date), ...forecast.map(f => f.date)])].sort()
            const actMap = {}
            actual.forEach(a => { actMap[a.date] = a })
            const headers = ['Date', 'Oil Actual (BBL)', 'Gas Actual (MCF)', 'Water Actual (BBL)',
                'Oil Forecast (BBL)', 'Gas Forecast (MCF)', 'Water Forecast (BBL)']
            const rows = allDates.map(d => [
                d,
                actMap[d]?.oil_bbl ?? '', actMap[d]?.gas_mcf ?? '', actMap[d]?.water_bbl ?? '',
                fcstMap[d]?.oil_bbl ?? '', fcstMap[d]?.gas_mcf ?? '', fcstMap[d]?.water_bbl ?? ''
            ])
            sheets.push({ name: 'Production History', data: [headers, ...rows] })
        }

        // Sheet 4: Workovers
        if (data.workovers?.length) {
            const headers = ['Well', 'Job Type', 'Start Date', 'End Date']
            const rows = data.workovers.map(w => [w.wellName || '', w.jobType || '', w.startDate || '', w.endDate || ''])
            sheets.push({ name: 'Workovers', data: [headers, ...rows] })
        }

        // Sheet 5: Downtime
        if (data.downtime?.length) {
            const headers = ['Well', 'Type', 'Start', 'End', 'Hours', 'Code', 'Comments']
            const rows = data.downtime.map(d => [d.wellName || '', d.downtimeType || '', d.startDate || '', d.endDate || '', d.downtimeHours ?? '', d.downtimeCode1 || '', d.comments || ''])
            sheets.push({ name: 'Downtime', data: [headers, ...rows] })
        }

        const safeName = (label || 'Well').replace(/[^a-zA-Z0-9 _-]/g, '').replace(/\s+/g, '_')
        exportMultiSheet(sheets, `Well_Analysis_${safeName}_${dateStr}`)
    }

    const DownloadBtn = ({ data, label }) => (
        <button onClick={() => handleDownload(data, label)} title="Download Excel"
            className={`p-1.5 rounded-lg border transition-colors ${isDark ? 'bg-slate-900 text-gray-300 border-slate-700 hover:bg-slate-800' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
        </button>
    )

    // Renders one detail panel: KPI + production chart + daily data grid (both visible)
    const renderDetail = (data, label, isLoading, isWellLevel) => {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
                </div>
            )
        }
        if (!data) {
            return (
                <div className={`chart-container flex items-center justify-center h-64 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    <p className="text-lg">Select a lease or well</p>
                </div>
            )
        }
        return (
            <div className="space-y-6">
                {/* KPI Table */}
                <div className="chart-container">
                    <KPITable kpis={data.kpis} isWellLevel={isWellLevel} />
                </div>

                {/* Production chart */}
                <div className="chart-container">
                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Production History
                            <span className={`ml-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                (Solid = Actual, Dashed = Forecast)
                            </span>
                        </h2>
                        <DownloadBtn data={data} label={label} />
                    </div>
                    <WellAnalysisChart
                        actualData={data.production?.actual || []}
                        forecastData={data.production?.forecast || []}
                        workovers={data.workovers || []}
                    />
                </div>

                {/* Daily data grid — directly below the chart */}
                <div className="chart-container">
                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Daily Data
                            <span className={`ml-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                (underlying production, injection &amp; pressure)
                            </span>
                        </h2>
                        <DownloadBtn data={data} label={label} />
                    </div>
                    <DailyDataTable rows={data.dailyGrid || []} dense={compareOn} />
                </div>
            </div>
        )
    }

    // Company-level production overview (landing page)
    const renderCompanyOverview = () => {
        if (!companyData) return null
        const k = companyData.kpis || {}
        const prev = companyData.previousWeek || {}
        const pct = (cur, p) => (p && p > 0) ? ((cur - p) / p) * 100 : null
        return (
            <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KPICard title="Company Oil (L7D)" value={(k.grossOilPerDay || 0).toLocaleString()} unit="BBL/d"
                        delta={pct(k.grossOilPerDay, prev.grossOilPerDay)} subtitle="Gross avg / day" />
                    <KPICard title="Company Gas (L7D)" value={(k.grossGasPerDay || 0).toLocaleString()} unit="MCF/d"
                        delta={pct(k.grossGasPerDay, prev.grossGasPerDay)} subtitle="Gross avg / day" />
                    <KPICard title="Company Water (L7D)" value={(k.grossWaterPerDay || 0).toLocaleString()} unit="BBL/d"
                        subtitle="Gross avg / day" />
                    <KPICard title="Active Wells" value={(k.activeWells || 0).toLocaleString()}
                        subtitle={`${companyData.counts?.leases || 0} leases · ${k.avgUptimePercent ?? '--'}% uptime`} />
                </div>

                <div className="chart-container">
                    <h2 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Company Production
                        <span className={`ml-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            (all wells — Solid = Actual, Dashed = Forecast)
                        </span>
                    </h2>
                    <WellAnalysisChart
                        actualData={companyData.production?.actual || []}
                        forecastData={companyData.production?.forecast || []}
                        workovers={[]}
                    />
                </div>
            </>
        )
    }

    // Top Under Performers table (shown on the landing page)
    const renderUnderperformers = () => {
        if (!underperformers || underperformers.length === 0) return null
        const periodLabel = upPeriod === '1d' ? '1D' : 'L7D'
        const dateLabel = upDataDate
            ? new Date(upDataDate + 'T12:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
            : ''
        const cols = [
            { key: 'comboName', label: 'Combo Name', align: 'left' },
            { key: 'oilForecast', label: 'Forecast', align: 'right' },
            { key: 'oilProduction', label: 'Production', align: 'right' },
            { key: 'delta', label: 'Delta', align: 'right' },
            { key: 'variancePct', label: 'Variance %', align: 'right' },
            { key: 'oilCut', label: 'Oil Cut', align: 'right' },
            { key: 'glr', label: 'GLR', align: 'right' }
        ]
        const sorted = [...underperformers].sort((a, b) => {
            const av = a[upSortCol] ?? 0, bv = b[upSortCol] ?? 0
            if (typeof av === 'string') return upSortAsc ? av.localeCompare(bv) : bv.localeCompare(av)
            return upSortAsc ? av - bv : bv - av
        })
        const handleSort = (col) => {
            if (upSortCol === col) setUpSortAsc(!upSortAsc)
            else { setUpSortCol(col); setUpSortAsc(col === 'comboName') }
        }
        const arrow = (col) => upSortCol === col ? (upSortAsc ? ' ▲' : ' ▼') : ''
        return (
            <div className="chart-container">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                    <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Top Under Performers
                        <span className={`ml-3 text-xs font-normal ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {upPeriod === '1d' ? dateLabel : `${periodLabel} Avg Daily`} — click a row to drill in
                        </span>
                    </h2>
                    <div className="flex items-center gap-1">
                        {['l7d', '1d'].map(p => (
                            <button key={p} onClick={() => handlePeriodChange(p)}
                                className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                                    upPeriod === p
                                        ? 'bg-blue-600 text-white'
                                        : isDark ? 'bg-slate-700 text-gray-300 hover:bg-slate-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}>
                                {p === 'l7d' ? '7 Day' : '1 Day'}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                {cols.map(c => (
                                    <th key={c.key}
                                        onClick={() => handleSort(c.key)}
                                        className={`${c.align === 'right' ? 'text-right' : ''} cursor-pointer select-none hover:text-blue-500 transition-colors whitespace-nowrap`}
                                    >
                                        {c.label}{arrow(c.key)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((up, i) => (
                                <tr key={i}
                                    className="cursor-pointer"
                                    onClick={() => { setSelectedCombo(up.comboName); setSelectedWell(null) }}
                                >
                                    <td className="font-medium">{up.comboName}</td>
                                    <td className="text-right font-mono">{Math.round(up.oilForecast).toLocaleString()}</td>
                                    <td className="text-right font-mono">{Math.round(up.oilProduction).toLocaleString()}</td>
                                    <td className={`text-right font-mono font-semibold ${
                                        up.delta < 0 ? isDark ? 'text-red-400' : 'text-red-600' : isDark ? 'text-green-400' : 'text-green-600'
                                    }`}>
                                        {Math.round(up.delta).toLocaleString()}
                                    </td>
                                    <td className={`text-right font-mono ${
                                        up.variancePct < 0 ? isDark ? 'text-red-400' : 'text-red-600' : isDark ? 'text-green-400' : 'text-green-600'
                                    }`}>
                                        {up.variancePct?.toFixed(1)}%
                                    </td>
                                    <td className="text-right font-mono">{up.oilCut?.toFixed(1)}%</td>
                                    <td className="text-right font-mono">{up.glr?.toFixed(0)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className={`text-[10px] mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {underperformers.length} combos — click a lease to drill into its detail
                </p>
            </div>
        )
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
            </div>
        )
    }

    const labelA = selectedWell || selectedCombo || 'A'
    const labelB = selectedWellB || selectedComboB || 'B'
    const inDetail = !!selectedCombo && !compareOn

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className={`border-b-2 ${isDark ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h1 className={`text-xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                            <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                            Well Analysis
                        </h1>
                        <p className={`${isDark ? 'text-blue-300' : 'text-blue-700'} text-xs sm:text-sm mt-1 ml-3 sm:ml-4`}>
                            {compareOn
                                ? `Comparing: ${labelA}  vs  ${selectedComboB ? labelB : '—'}`
                                : (selectedWell || selectedCombo || 'Company overview — pick a lease or well to drill in')}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {inDetail && (
                            <button onClick={clearSelection}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border-2 transition-colors ${
                                    isDark ? 'bg-slate-800 text-gray-200 border-slate-600 hover:bg-slate-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}>
                                ← Company Overview
                            </button>
                        )}
                        <button onClick={toggleCompare}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg border-2 transition-colors ${
                                compareOn
                                    ? 'bg-blue-600 text-white border-blue-500'
                                    : isDark ? 'bg-slate-800 text-gray-200 border-slate-600 hover:bg-slate-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}>
                            {compareOn ? '✕ Exit Compare' : '⇄ Compare'}
                        </button>
                    </div>
                </div>

                {/* Compare picker */}
                {compareOn && (
                    <div className="mt-3 ml-3 sm:ml-4 flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {labelA} <span className="mx-1 font-bold">vs</span>
                        </span>
                        <CompareSelect
                            combos={selectorData || []}
                            valueLabel={selectedComboB ? labelB : ''}
                            onSelect={handleCompareSelect}
                        />
                    </div>
                )}
            </div>

            {/* Main layout: content + sidebar */}
            <div className="flex flex-col lg:flex-row gap-4">
                {/* Left: main content */}
                <div className="flex-1 min-w-0 space-y-6">
                    {compareOn ? (
                        /* Compare: side-by-side panels */
                        <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
                            <div className="min-w-0">
                                <div className={`text-sm font-bold mb-2 px-1 ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>{selectedCombo ? labelA : 'Pick a primary lease/well (sidebar)'}</div>
                                {selectedCombo
                                    ? renderDetail(comboData, labelA, comboLoading, !!selectedWell)
                                    : (
                                        <div className={`chart-container flex items-center justify-center h-64 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                            <p className="text-base">Use the sidebar to pick side A</p>
                                        </div>
                                    )}
                            </div>
                            <div className="min-w-0">
                                <div className={`text-sm font-bold mb-2 px-1 ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>{selectedComboB ? labelB : 'Pick a second lease/well above'}</div>
                                {selectedComboB
                                    ? renderDetail(comboDataB, labelB, comboLoadingB, !!selectedWellB)
                                    : (
                                        <div className={`chart-container flex items-center justify-center h-64 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                            <p className="text-base">Use the “Compare with…” box above</p>
                                        </div>
                                    )}
                            </div>
                        </div>
                    ) : selectedCombo ? (
                        /* Detail view for a selected lease/well */
                        renderDetail(comboData, labelA, comboLoading, !!selectedWell)
                    ) : (
                        /* Landing: company overview + top under performers */
                        <>
                            {renderCompanyOverview()}
                            {renderUnderperformers()}
                        </>
                    )}

                    {error && (
                        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
                            <p className="font-semibold">Error</p>
                            <p className="text-sm mt-1">{error}</p>
                            <button onClick={() => { setError(null); if (selectedCombo) fetchDetail() }}
                                className="mt-3 text-sm bg-red-800 hover:bg-red-700 text-white px-3 py-1 rounded transition">
                                Retry
                            </button>
                        </div>
                    )}
                </div>

                {/* Right sidebar: selector (controls A) */}
                <div className="lg:w-80 xl:w-96 flex-shrink-0 order-first lg:order-last">
                    <ComboWellSelector
                        combos={selectorData || []}
                        selectedCombo={selectedCombo}
                        selectedWell={selectedWell}
                        onComboChange={setSelectedCombo}
                        onWellChange={setSelectedWell}
                    />
                </div>
            </div>
        </div>
    )
}

export default WellAnalysis
